import React, { useState, useEffect } from 'react';
import { useAlert } from '../context/AlertContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Plus, Trash2, Edit, Search, ChevronRight, Clock, DollarSign } from 'lucide-react';
import { Table, Button, InputGroup, Form, Card, Row, Col } from 'react-bootstrap';
import './ServiceList.css';

interface Service {
    id: number;
    name: string;
    duration: string;
    price: string;
}

const ServiceList = () => {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [services, setServices] = useState<Service[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        const response = await api.get('/services/');
        setServices(response.data);
    };

    const handleDelete = (id: number) => {
        showAlert('Tem certeza que deseja excluir?', {
            title: 'Confirmação',
            confirmable: true,
            confirmText: 'Excluir',
            confirmVariant: 'btn-primary',
            onConfirm: async () => {
                await api.delete(`/services/${id}/`);
                fetchServices();
            }
        });
    };

    const filteredServices = services.filter(s =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Card className="shadow-sm border-0">
            <Card.Body>
                <Row className="mb-4 align-items-center">
                    <Col>
                        <h3 className="mb-0">Serviços</h3>
                    </Col>
                    <Col xs="auto">
                        <Button variant="success" onClick={() => navigate('/services/new')} className="d-flex align-items-center gap-2">
                            <Plus size={18} /> Novo Serviço
                        </Button>
                    </Col>
                </Row>

                <InputGroup className="mb-4">
                    <InputGroup.Text className="bg-white border-end-0">
                        <Search size={18} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                        placeholder="Pesquisar por nome..."
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        className="border-start-0 ps-0"
                    />
                </InputGroup>

                {/* Desktop: Tabela */}
                <div className="custom-table-container d-none d-md-block">
                    <Table hover className="custom-table mb-0">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Duração</th>
                                <th>Preço</th>
                                <th className="text-end">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredServices.map((service) => (
                                <tr
                                    key={service.id}
                                >
                                    <td className="fw-medium" onClick={() => navigate(`/services/${service.id}`)} style={{ cursor: 'pointer' }}>{service.name}</td>
                                    <td onClick={() => navigate(`/services/${service.id}`)} style={{ cursor: 'pointer' }}>{service.duration}</td>
                                    <td onClick={() => navigate(`/services/${service.id}`)} style={{ cursor: 'pointer' }}>
                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(service.price))}
                                    </td>
                                    <td className="text-end">
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1 me-2"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                navigate(`/services/${service.id}`);
                                            }}
                                        >
                                            <Edit size={18} />
                                        </Button>
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                handleDelete(service.id);
                                            }}
                                        >
                                            <Trash2 size={18} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {filteredServices.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="text-center py-4 text-muted">
                                        Nenhum serviço encontrado.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* Mobile: Lista de Cards */}
                <div className="service-list-mobile d-md-none">
                    {filteredServices.length > 0 ? (
                        filteredServices.map((service) => (
                            <div key={service.id} className="service-card-mobile">
                                <div 
                                    className="service-card-mobile-content"
                                    onClick={() => navigate(`/services/${service.id}`)}
                                >
                                    <div className="service-card-mobile-info">
                                        <span className="service-card-mobile-name">{service.name}</span>
                                        <div className="service-card-mobile-details">
                                            <span className="service-card-mobile-detail">
                                                <Clock size={14} /> {service.duration}
                                            </span>
                                            <span className="service-card-mobile-detail">
                                                <DollarSign size={14} /> {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(service.price))}
                                            </span>
                                        </div>
                                    </div>
                                    <ChevronRight size={20} className="text-muted" />
                                </div>
                                <div className="service-card-mobile-actions">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="service-card-mobile-btn"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            navigate(`/services/${service.id}`);
                                        }}
                                    >
                                        <Edit size={16} /> Editar
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="service-card-mobile-btn"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            handleDelete(service.id);
                                        }}
                                    >
                                        <Trash2 size={16} /> Excluir
                                    </Button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-4 text-muted">
                            Nenhum serviço encontrado.
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default ServiceList;
